import React from "react";
import { useRecordContext, useGetManyAggregate, useGetList } from "react-admin";
import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { bwColours } from "../lib/theme";
import { useResizeObserver } from "./utils";
import moment from "moment";
import type { Selection } from "d3";
import { custInfo } from "../lib/customer";
import { densityToOechsle, densityToBrix } from "../lib/density";
import {
  cToF,
  prefersFahrenheit,
  snapToF,
  temperatureUnit,
} from "../lib/temperature";

import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// x: hours relative to the start of the ferment
// y: the quantity of interest
export interface DataPoint {
  x: number;
  y: number | null;
}

export const FermentPlot = ({
  startTime, // real wallclock Date() for the start of the plot
  xMax, // number of hours for the x-axis range
  dataSeries, // should be an array of {x: [hours], y: [value]}; may be empty
  // (or undefined) when the plot only shows reference points
  predictionSeries, // should be an array of {x: [hours], y: [value]}
  referenceSeries, // should be an array of {x: [hours], y: [value]}
  actuations, // actuation state displays: {series: {x: [hours], y: [0|1]},
  // colour, opacity}; the hours in which an actuation was active are shown as
  // bands of the given colour at the given opacity (the same display as the
  // smart tank charts)
  setPoint,
  units,
  yMin,
  yMax,
  yLabel,
  colour,
  mini,
  precision = 1,
}: {
  startTime: Date;
  xMax: number;
  dataSeries?: DataPoint[];
  predictionSeries?: DataPoint[] | undefined;
  referenceSeries?: DataPoint[];
  actuations?: { series: DataPoint[]; colour: string; opacity: number }[];
  setPoint?: number | null;
  units: string;
  // Optional: when neither is given, the range is derived from the visible
  // series (used for plots of arbitrary analysis types).
  yMin?: number;
  yMax?: number;
  yLabel: string;
  colour: string;
  mini: boolean;
  precision?: number;
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null!);
  const dimensions = useResizeObserver(wrapperRef);

  const styles = {
    container: {
      marginTop: mini ? 0 : 40,
      marginBottom: mini ? 0 : 80,
      position: "relative" as const,
      zIndex: 1,
    },
    svg: {
      width: "100%",
      height: mini ? 100 : 225,
      overflow: "visible",
      // On touch devices: let vertical drags scroll the page (the
      // chart previously called preventDefault on touchstart, which
      // trapped page scrolling) while horizontal drags still scrub
      // the tooltip via the pointer events below.
      touchAction: "pan-y",
    },
  };

  useEffect(() => {
    if (!dimensions) return;

    const series = dataSeries ?? [];
    const hasData = series.length > 0;

    const showPrediction =
      predictionSeries && predictionSeries.length > 0 && !mini;

    const showReference =
      referenceSeries && referenceSeries.length > 0 && !mini;

    const showActuation = actuations !== undefined && actuations.length > 0;

    // Only draw anything if at least one series has points; the plot does
    // not require the main data series to be present (e.g. a density plot
    // with only lab-measured reference points).
    if (!hasData && !showPrediction && !showReference && !showActuation) return;

    const margin = {
      top: 0,
      left: mini ? 0 : 60,
      bottom: 0,
      right: 0,
    };

    let canvas = d3.select(svgRef.current);
    canvas.selectAll("*").remove();

    // Don't render if the effective width is too small
    if (dimensions.width - margin.left - margin.right <= 0) {
      return;
    }

    // chart background
    if (!mini) {
      canvas
        .append("rect")
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", dimensions.width - margin.left - margin.right)
        .attr("height", dimensions.height - margin.top - margin.bottom)
        .attr("fill", "rgb(250,250,251)");
    }

    canvas
      .on("pointerenter pointermove", pointermoved)
      .on("pointerleave", pointerleft)
      // Hide the tooltip if the browser takes over the gesture
      // (e.g. a vertical pan that scrolls the page).
      .on("pointercancel", pointerleft);

    // Extents of each series that is present; absent series contribute
    // undefined, which d3.min/d3.max skip, so the auto-range covers exactly
    // the series on the plot (the reference points included, even when the
    // main data series is empty).
    const [dataSeriesMin, dataSeriesMax] = hasData
      ? d3.extent(
          series.map(function (d) {
            return d.y;
          }),
        )
      : [undefined, undefined];

    const [predictionSeriesMin, predictionSeriesMax] = showPrediction
      ? d3.extent(
          predictionSeries.map(function (d) {
            return d.y;
          }),
        )
      : [undefined, undefined];

    const [referenceSeriesMin, referenceSeriesMax] = showReference
      ? d3.extent(
          referenceSeries.map(function (d) {
            return d.y;
          }),
        )
      : [undefined, undefined];

    // yMin/yMax bound the range from below/above; the visible series extend
    // them. When neither bound is given (arbitrary analysis types), the range
    // is derived entirely from the visible series, padded a little so the
    // line does not touch the axis edges.
    let yLow = d3.min([
      yMin,
      dataSeriesMin,
      predictionSeriesMin,
      referenceSeriesMin,
    ]);
    let yHigh = d3.max([
      yMax,
      dataSeriesMax,
      predictionSeriesMax,
      referenceSeriesMax,
    ]);
    if (
      yMin === undefined &&
      yMax === undefined &&
      yLow !== undefined &&
      yHigh !== undefined
    ) {
      if (yHigh - yLow === 0) {
        // All visible points are identical: expand around the value
        const spread = Math.max(Math.abs(yLow) * 0.1, 1);
        yLow -= spread;
        yHigh += spread;
      } else {
        const padding = (yHigh - yLow) * 0.05;
        yLow -= padding;
        yHigh += padding;
      }
    }

    let yScale = d3
      .scaleLinear()
      .domain([yLow, yHigh])
      .range([dimensions.height - margin.bottom, margin.top]);

    let xScale = d3
      .scaleLinear()
      .domain([0, xMax])
      .range([margin.left + 3, dimensions.width - margin.right]);

    // Actuation state fill: a band for the hours in which each actuation
    // was active (y >= 0.5), the same display as the smart tank charts.
    // Drawn before the axes and data so it sits behind them. Bands are drawn
    // at the given opacity, so overlapping bands blend darker.
    if (showActuation) {
      for (const actuation of actuations) {
        const series = actuation.series;
        const isOn = (d: DataPoint) => d.y !== null && d.y >= 0.5;
        const onPeriods: { startX: number; endX: number }[] = [];
        let startX: number | null =
          series.length > 0 && isOn(series[0]) ? series[0].x : null;

        for (let i = 1; i < series.length; i++) {
          if (isOn(series[i]) && !isOn(series[i - 1])) {
            startX = series[i].x;
          } else if (
            isOn(series[i - 1]) &&
            !isOn(series[i]) &&
            startX !== null
          ) {
            onPeriods.push({ startX, endX: series[i].x });
            startX = null;
          }
        }
        if (startX !== null) {
          // Still on at the last point: extend one more hour so the band is
          // visible.
          onPeriods.push({
            startX,
            endX: series[series.length - 1].x + 1,
          });
        }

        canvas
          .append("g")
          .attr("class", "actuation-state")
          .selectAll("rect")
          .data(onPeriods)
          .join("rect")
          .attr("x", (d) => xScale(d.startX))
          .attr("y", margin.top)
          .attr("width", (d) => xScale(d.endX) - xScale(d.startX))
          .attr("height", dimensions.height - margin.top - margin.bottom)
          .attr("fill", actuation.colour)
          .attr("opacity", actuation.opacity);
      }
    }

    // Ticks every 24 hours by default; for very long plots widen the step so
    // the axis renders a bounded number of ticks (a tick per 24 hours over an
    // abnormally large xMax would be hundreds of thousands of SVG nodes).
    const tickStep = Math.max(24, Math.ceil(xMax / 30 / 24) * 24);

    // Add the x-axis.
    canvas
      .append("g")
      .attr("transform", `translate(0,${dimensions.height - margin.bottom})`)
      .attr("opacity", 0.7)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(d3.range(0, xMax + 1, tickStep))
          .tickFormat((x: number) => {
            return mini ? "" : x / 24;
          })
          .tickSizeOuter(0)
          .tickSizeInner(6),
      )
      .call((g: Selection<SVGElement, unknown, null, null>) =>
        g.select(".domain").remove(),
      )
      .call((g: Selection<SVGElement, unknown, null, null>) =>
        g.selectAll(".tick line").attr("stroke", "#ccc"),
      );

    if (!mini) {
      // Add the y-axis, remove the domain line, add grid lines and a label.
      canvas
        .append("g")
        .attr("transform", `translate(${margin.left},0)`)
        .attr("opacity", 0.7)
        .call(
          d3
            .axisLeft(yScale)
            .ticks(dimensions.height / 40)
            .tickFormat((d: number) => d),
        )
        .call((g: Selection<SVGElement, unknown, null, null>) =>
          g.select(".domain").remove(),
        )
        .call((g: Selection<SVGElement, unknown, null, null>) =>
          g
            .selectAll(".tick line")
            .attr("x2", dimensions.width - margin.left - margin.right)
            .attr("stroke-opacity", 0.1),
        )
        .call((g: Selection<SVGElement, unknown, null, null>) =>
          g
            .append("text")
            .attr("transform", "rotate(-90)")
            .attr("font-size", 16)
            .attr("y", -40)
            .attr("x", -(dimensions.height / 2))
            .attr("text-anchor", "middle")
            .attr("fill", "currentColor")
            .attr("opacity", 0.9)
            .text(yLabel),
        );
    }

    // Process data: separate isolated points from continuous segments,
    // and identify gaps < 12 hours for interpolation
    const GAP_THRESHOLD = 12; // hours - interpolate if gap is less than this
    const ISOLATED_THRESHOLD = GAP_THRESHOLD / 2; // point is "isolated" if it has no neighbor within this distance

    const validDataPoints = series.filter(
      (d: DataPoint) => d.y !== null && isFinite(d.y),
    );
    validDataPoints.sort((a: DataPoint, b: DataPoint) => a.x - b.x);

    // Identify isolated points (no neighbor within threshold)
    const isolatedPoints = validDataPoints.filter((d: DataPoint, i: number) => {
      const prev = i > 0 ? validDataPoints[i - 1] : null;
      const next =
        i < validDataPoints.length - 1 ? validDataPoints[i + 1] : null;
      const prevDist = prev ? d.x - prev.x : Infinity;
      const nextDist = next ? next.x - d.x : Infinity;
      return prevDist > ISOLATED_THRESHOLD && nextDist > ISOLATED_THRESHOLD;
    });

    // Build continuous segments (groups of points within threshold of each other)
    const segments: { x: number; y: number }[][] = [];
    const usedIndices = new Set<number>();
    for (let i = 0; i < validDataPoints.length; i++) {
      if (usedIndices.has(i)) continue;
      if (isolatedPoints.includes(validDataPoints[i])) {
        usedIndices.add(i);
        continue;
      }
      const segment: { x: number; y: number }[] = [validDataPoints[i]];
      usedIndices.add(i);
      for (let j = i + 1; j < validDataPoints.length; j++) {
        if (usedIndices.has(j)) break;
        const dist = validDataPoints[j].x - segment[segment.length - 1].x;
        if (dist > GAP_THRESHOLD) break;
        segment.push(validDataPoints[j]);
        usedIndices.add(j);
      }
      segments.push(segment);
    }

    // Render continuous segments
    for (const segment of segments) {
      canvas
        .append("path")
        .datum(segment)
        .attr(
          "d",
          d3
            .line()
            .defined((d: DataPoint) => d.y !== null)
            .x((d: DataPoint) => xScale(d.x))
            .y((d: DataPoint) => yScale(d.y))
            .curve(d3.curveBasis),
        )
        .attr("fill", "none")
        .attr("stroke", colour)
        .attr("stroke-width", 2);
    }

    // Render isolated points as dots
    for (const pt of isolatedPoints) {
      canvas
        .append("circle")
        .attr("cx", xScale(pt.x))
        .attr("cy", yScale(pt.y))
        .attr("r", 2)
        .attr("fill", colour)
        .attr("stroke", "white")
        .attr("stroke-width", 1);
    }

    // Prediction curve
    if (showPrediction) {
      canvas
        .append("path")
        .datum(predictionSeries)
        .attr(
          "d",
          d3
            .line()
            .defined((d: DataPoint) => d.y !== null)
            .x((d: DataPoint) => xScale(d.x))
            .y((d: DataPoint) => yScale(d.y))
            .curve(d3.curveBasis),
        )
        .attr("fill", "none")
        .attr("stroke", colour)
        .attr("stroke-width", 1)
        .attr("opacity", 0.5);
    }

    // Reference curve
    if (showReference) {
      canvas
        .append("g")
        .selectAll("dot")
        .data(referenceSeries)
        .enter()
        .append("circle")
        .attr("cx", (d: DataPoint) => xScale(d.x))
        .attr("cy", (d: DataPoint) => yScale(d.y))
        .attr("r", 3)
        .style("fill", "black");
    }

    // Set point
    if (setPoint !== undefined && setPoint !== null) {
      canvas
        .append("line")
        .attr("x1", xScale(xMax) - 50)
        .attr("y1", yScale(setPoint))
        .attr("x2", xScale(xMax))
        .attr("y2", yScale(setPoint))
        .attr("stroke", colour)
        .attr("stroke-width", 3);
      canvas
        .append("text")
        .attr("text-anchor", "end")
        .attr("font-size", 12)
        .attr("x", xScale(xMax))
        .attr("y", yScale(setPoint) - 26)
        .text(`Setpoint`);
      canvas
        .append("text")
        .attr("text-anchor", "end")
        .attr("font-size", 12)
        .attr("x", xScale(xMax))
        .attr("y", yScale(setPoint) - 9)
        .text(
          `${Number.isInteger(setPoint) ? setPoint : setPoint.toFixed(precision)}${units}`,
        );
    }

    // Final value label
    if (validDataPoints.length > 0) {
      const lastPoint = validDataPoints[validDataPoints.length - 1];
      canvas
        .append("text")
        .attr("x", xScale(lastPoint.x) + 8)
        .attr("y", yScale(lastPoint.y))
        .attr("dominant-baseline", "middle")
        .attr("font-size", 12)
        .attr("fill", colour)
        .text(
          `${Number.isInteger(lastPoint.y) ? lastPoint.y : lastPoint.y.toFixed(precision)}${units}`,
        );
    }

    // Tooltip
    const tooltip = canvas.append("g");

    const formatDate = (x: number) => {
      const date = new Date(startTime);
      date.setHours(startTime.getHours() + x);
      const beginning = moment(date).format("llll");
      date.setHours(startTime.getHours() + x + 1);
      const ending = moment(date).format("LT");
      return beginning + " — " + ending;
    };
    const formatValue = (y) => {
      return `${y}${units}`;
    };

    // bisect, but search up to ISOLATED_THRESHOLD datapoints away
    const bisect = d3.bisector((d: DataPoint) => d.x).center;
    function findNearest<T extends { x: number; y: number | null }>(
      data: T[],
      targetX: number,
    ) {
      if (data.length === 0) return undefined;
      let targetIdx = bisect(data, targetX);
      for (let i = 0; i <= ISOLATED_THRESHOLD; i++) {
        const j = Math.min(targetIdx + i, data.length - 1);
        if (
          data[j].y !== null &&
          isFinite(data[j].y) &&
          Math.abs(data[j].x - targetX) <= ISOLATED_THRESHOLD
        ) {
          return j;
        }
        const k = Math.max(targetIdx - i, 0);
        if (
          data[k].y !== null &&
          isFinite(data[k].y) &&
          Math.abs(data[k].x - targetX) <= ISOLATED_THRESHOLD
        ) {
          return k;
        }
      }
      return undefined;
    }

    // Add the event listeners that show or hide the tooltip.
    function pointermoved(event: MouseEvent) {
      if (wrapperRef.current) {
        // Boost the zIndex of this entire chart to ensure the tooltip is higher
        // than all other instances of the plot.
        wrapperRef.current.style.zIndex = "10";
      }
      const x = xScale.invert(d3.pointer(event)[0]);

      const i = hasData ? findNearest(series, x) : undefined;
      const i_pred = showPrediction
        ? findNearest(predictionSeries, x)
        : undefined;

      const datum = i !== undefined ? series[i] : undefined;
      const datumPred =
        i_pred !== undefined ? predictionSeries[i_pred] : undefined;
      // Reference points are sparse lab measurements. With the data series
      // present, only surface one when the pointer is very close (1 hour),
      // so a stale lab reading is never shown next to a live one. When the
      // reference points stand alone on the plot, show the nearest one
      // regardless of distance, so the plot stays responsive between
      // measurements.
      let datumRef: DataPoint | undefined = undefined;
      if (showReference) {
        if (hasData) {
          const i_ref = findNearest(referenceSeries, x);
          datumRef =
            i_ref !== undefined && Math.abs(referenceSeries[i_ref].x - x) <= 1
              ? referenceSeries[i_ref]
              : undefined;
        } else {
          for (const d of referenceSeries) {
            if (d.y === null || !isFinite(d.y)) continue;
            if (
              datumRef === undefined ||
              Math.abs(d.x - x) < Math.abs(datumRef.x - x)
            ) {
              datumRef = d;
            }
          }
        }
      }

      // Hide tooltip if no series has a point within range
      if (
        datum === undefined &&
        datumPred === undefined &&
        datumRef === undefined
      ) {
        tooltip.style("display", "none");
        return;
      }

      // Anchor the tooltip on whichever candidate is closest to the pointer.
      let anchor: DataPoint | undefined = undefined;
      for (const candidate of [datum, datumPred, datumRef]) {
        if (candidate === undefined) continue;
        if (
          anchor === undefined ||
          Math.abs(candidate.x - x) < Math.abs(anchor.x - x)
        ) {
          anchor = candidate;
        }
      }
      if (anchor === undefined) {
        tooltip.style("display", "none");
        return;
      }

      // One row per series that has a point close enough to the pointer.
      // (Every candidate was filtered on a valid y value above; the checks
      // below also narrow the type.)
      const datalines: { text: string; opacity: number }[] = [
        { text: formatDate(anchor.x), opacity: 1 },
      ];
      if (
        datum !== undefined &&
        datum.y !== null &&
        isFinite(datum.y) &&
        (!showPrediction || Math.abs(datum.x - x) < 1)
      ) {
        datalines.push({
          text: formatValue(datum.y.toFixed(precision)),
          opacity: 1,
        });
      }
      if (
        datumPred !== undefined &&
        datumPred.y !== null &&
        isFinite(datumPred.y)
      ) {
        datalines.push({
          text: formatValue(datumPred.y.toFixed(precision)) + " predicted",
          opacity: 0.5,
        });
      }
      if (
        datumRef !== undefined &&
        datumRef.y !== null &&
        isFinite(datumRef.y)
      ) {
        datalines.push({
          text: formatValue(datumRef.y.toFixed(precision)) + " reference",
          opacity: 1,
        });
      }

      // Show and position the tooltip
      tooltip.style("display", null);
      tooltip.attr(
        "transform",
        `translate(${xScale(anchor.x)},${yScale(anchor.y ?? 0)})`,
      );

      const path: Selection<SVGElement, undefined, SVGElement, null> = tooltip
        .selectAll("path")
        .data([undefined])
        .join("path")
        .attr("fill", "rgba(255,255,255,0.95)")
        .attr("stroke", "#ccc");

      const text: Selection<SVGTextElement, undefined, SVGElement, null> =
        tooltip
          .selectAll("text")
          .data([undefined])
          .join("text")
          .call(
            (text: Selection<SVGTextElement, undefined, SVGElement, null>) =>
              text
                .selectAll("tspan")
                .data(datalines)
                .join("tspan")
                .attr("x", 0)
                .attr("y", (_, i) => `${i * 1.1}em`)
                .attr("font-weight", (_, i: number) => (i ? null : "bold"))
                .attr("opacity", (d) => d.opacity)
                .text((d) => d.text),
          );

      const offset = anchor.x / xMax - 0.5;
      // Show tooltip above the datapoint when it's in the lower half of the plot
      const above = yScale(anchor.y ?? 0) > dimensions.height / 2;
      size(text, path, -offset, above);
    }

    function pointerleft() {
      if (wrapperRef.current) {
        // Reset the zIndex of this plot
        wrapperRef.current.style.zIndex = "1";
      }
      tooltip.style("display", "none");
    }

    // Wraps the text with a callout path of the correct size, as measured in the page.
    function size(
      text: Selection<SVGTextElement, undefined, SVGElement, null>,
      path: Selection<SVGElement, undefined, SVGElement, null>,
      offset: number,
      above: boolean,
    ) {
      const { y, width: w, height: h } = text.node().getBBox();
      offset *= w;

      if (above) {
        // Tooltip above the datapoint: text sits just above the anchor,
        // path box extends upward with notch at the bottom pointing to anchor.
        text.attr("transform", `translate(${-w / 2 + offset},${-h - 5})`);
        // Build from scratch: start at bottom-left of box, go left to notch,
        // down into notch tip (y=0), back up to notch opening, right along
        // bottom edge, up around box, close.
        // "below" notch: l5,-5 l5,5  (up into notch, down out)
        // "above" notch: l5,5 l5,-5   (down into notch, up out)
        // "below" box move: v h+20  (down); "above" box move: v -(h+20) (up)
        path.attr(
          "d",
          `M${-w / 2 - 10 + offset},-5H-5l5,5l5,-5H${w / 2 + 10 + offset}v-${h + 20}h${-(w + 20)}z`,
        );
      } else {
        text.attr("transform", `translate(${-w / 2 + offset},${15 - y})`);
        path.attr(
          "d",
          `M${-w / 2 - 10 + offset},5H-5l5,-5l5,5H${w / 2 + 10 + offset}v${h + 20}h-${w + 20}z`,
        );
      }
    }
  }, [
    startTime,
    xMax,
    dataSeries,
    predictionSeries,
    referenceSeries,
    actuations,
    setPoint,
    units,
    dimensions,
    yMin,
    yMax,
    yLabel,
    colour,
    mini,
    precision,
  ]);

  return (
    <div ref={wrapperRef} style={styles.container}>
      <svg ref={svgRef} style={styles.svg} />
    </div>
  );
};

const calculateXmax = (data, addl = 0) => {
  return d3.max([
    3 * 24,
    (data.temperatureSeries ?? []).length + 24,
    (data.densSeries ?? []).length + 24,
    (data.predictedDuration ?? 0) + 3 * 24,
    addl + 24,
  ]);
};

export const TemperaturePlot = ({
  mini,
  label: _label,
}: {
  mini: boolean;
  label?: string;
}) => {
  const record = useRecordContext();
  const { data: devices } = useGetManyAggregate("devices", {
    ids: record?.deviceID ? [record.deviceID] : [],
  });

  if (!record) return null;
  const data = record.data;

  // Temperatures are stored in °C; convert to °F only for presentation.
  const useF = prefersFahrenheit();
  const toDisplay = (y: number) => (useF ? cToF(y) : y);

  let setpoint: number | undefined = undefined;
  if (devices && devices.length > 0) {
    const temp_ctl_enable = devices[0].settings["temp_ctl_enable"];
    const temp_mode = devices[0].settings["temp_mode"];
    const temp_setpoint = devices[0].settings["temp_setpoint"];

    if (temp_ctl_enable && temp_mode) {
      // Setpoints use the controller firmware's snap-to-integer-F algorithm.
      setpoint = useF ? snapToF(temp_setpoint / 10.0) : temp_setpoint / 10.0;
    }
  }

  if (!data.temperatureSeries) return;

  // Cooling and heating are drawn as their own coloured bands (blue and
  // red), matching the smart tank temperature chart.
  const actuations = [
    {
      series: (data.coolingStateSeries ?? []).map((y: number, x: number) => ({
        x,
        y,
      })),
      colour: bwColours.accent.blue,
      opacity: 0.12,
    },
    {
      series: (data.heatingStateSeries ?? []).map((y: number, x: number) => ({
        x,
        y,
      })),
      colour: bwColours.accent.red,
      opacity: 0.12,
    },
  ];

  return (
    <FermentPlot
      startTime={new Date(data.startTime)}
      xMax={calculateXmax(data)}
      dataSeries={data.temperatureSeries.map((y: number, x: number) => ({
        x: x,
        y: toDisplay(y),
      }))}
      actuations={actuations}
      setPoint={!mini && !record.endTime ? setpoint : undefined}
      units={temperatureUnit()}
      yMin={toDisplay(-5)}
      yMax={toDisplay(30)}
      yLabel={`Temperature (${temperatureUnit()})`}
      colour={bwColours.accent.blue}
      mini={mini}
      predictionSeries={undefined}
      referenceSeries={undefined}
    />
  );
};

export const densityPlotConfigs = {
  "g/L": {
    convertValue: (y: number) => y,
    yConfig: {
      units: "g/L",
      yMin: 990,
      yMax: 1110,
      yLabel: "Density (g/L)",
      precision: 0,
    },
  },
  "°Oe": {
    convertValue: (y: number) => densityToOechsle(y)!,
    yConfig: {
      units: "°Oe",
      yMin: -12,
      yMax: 110,
      yLabel: "Oechsle (°Oe)",
      precision: 1,
    },
  },
  "°Bx": {
    convertValue: (y: number) => densityToBrix(y)!,
    yConfig: {
      units: "°Bx",
      yMin: -3,
      yMax: 26,
      yLabel: "Brix (°Bx)",
      precision: 1,
    },
  },
};

export const DensityPlot = ({
  mini,
  label: _label,
  referenceSeries,
}: {
  mini: boolean;
  label?: string;
  referenceSeries?: DataPoint[];
}) => {
  const densityUnit = custInfo.customerPreferences.densityUnit;
  const config =
    densityPlotConfigs[densityUnit as keyof typeof densityPlotConfigs] ??
    densityPlotConfigs["°Bx"];
  return (
    <DensityChart
      mini={mini}
      label={_label}
      config={config}
      referenceSeries={referenceSeries}
    />
  );
};

const ShowModelSwitch = ({
  showModel,
  onToggle,
}: {
  showModel: boolean;
  onToggle: (checked: boolean) => void;
}) => (
  <FormGroup
    style={{
      position: "absolute",
      right: 0,
      top: 10,
      paddingLeft: 12,
      paddingRight: 12,
      borderRadius: 50,
      zIndex: 20,
    }}
  >
    <FormControlLabel
      control={
        <Switch
          checked={showModel}
          onChange={(_, checked) => onToggle(checked)}
        />
      }
      label="Predictive Model"
    />
  </FormGroup>
);

interface DensityChartConfig {
  convertValue: (y: number) => number;
  yConfig: {
    units: string;
    yMin: number;
    yMax: number;
    yLabel: string;
    precision?: number;
  };
}

const DensityChart = ({
  mini,
  label: _label,
  config,
  referenceSeries,
}: {
  mini: boolean;
  label?: string;
  config: DensityChartConfig;
  referenceSeries?: DataPoint[];
}) => {
  const [showModel, setShowModel] = useState(false);
  const record = useRecordContext();

  if (!record) return null;
  const data = record.data;

  const hasDensitySeries = !!data.densSeries && data.densSeries.length > 0;
  const hasReferenceSeries =
    !mini && !!referenceSeries && referenceSeries.length > 0;

  // A ferment with no hydrometer readings can still show a plot of just
  // the lab-measured reference points.
  if (!hasDensitySeries && !hasReferenceSeries) return null;

  // The x-axis origin: the start of the device data when the ferment has any,
  // otherwise the start of the ferment itself (ferments without a smart tank
  // have no data start time).
  const startTime = new Date(data.startTime ?? record.startTime);

  // Keep the x-axis wide enough to contain every reference point, even when
  // the hydrometer series is empty.
  const referenceMaxX = hasReferenceSeries
    ? Math.round(d3.max(referenceSeries.map((d: DataPoint) => d.x)) ?? 0)
    : 0;

  const predictedSeries =
    data.mvModelling &&
    data.mvModelling.body &&
    data.mvModelling.body.ModelResults &&
    data.mvModelling.body.ModelResults.DensBest
      ? data.mvModelling.body.ModelResults.DensBest
      : [];

  return (
    <div style={{ position: "relative" }}>
      <FermentPlot
        startTime={startTime}
        xMax={calculateXmax(data, referenceMaxX)}
        dataSeries={
          hasDensitySeries
            ? data.densSeries.map((y: number, x: number) => ({
                x: x,
                y: config.convertValue(y),
              }))
            : []
        }
        predictionSeries={
          showModel && predictedSeries.length > 0
            ? predictedSeries.map((y: number, x: number) => ({
                x: x * 2,
                y: config.convertValue(y),
              }))
            : undefined
        }
        referenceSeries={referenceSeries}
        units={config.yConfig.units}
        yMin={config.yConfig.yMin}
        yMax={config.yConfig.yMax}
        yLabel={config.yConfig.yLabel}
        colour={bwColours.accent.green}
        mini={mini}
        setPoint={undefined}
        precision={config.yConfig.precision}
      />
      {!mini && predictedSeries.length > 0 ? (
        <ShowModelSwitch showModel={showModel} onToggle={setShowModel} />
      ) : null}
    </div>
  );
};

export const RedoxPlot = ({
  mini,
  label: _label,
}: {
  mini: boolean;
  label?: string;
}) => {
  const record = useRecordContext();
  const { data: devices } = useGetManyAggregate("devices", {
    ids: record?.deviceID ? [record.deviceID] : [],
  });

  if (!record) return null;
  const data = record.data;

  let setpoint: number | undefined = undefined;
  if (devices && devices.length > 0) {
    const sparge_enable = devices[0].settings["sparge_enable"];
    const sparge_mode = devices[0].settings["sparge_mode"];
    const sparge_setpoint = devices[0].settings["sparge_setpoint"];

    if (sparge_enable && sparge_mode) {
      setpoint = sparge_setpoint;
    }
  }

  if (!data.redoxSeries) return;

  return (
    <FermentPlot
      startTime={new Date(data.startTime)}
      xMax={calculateXmax(data)}
      dataSeries={data.redoxSeries.map((y: number, x: number) => ({
        x: x,
        y: y,
      }))}
      // Sparge actuation display of sparge events
      actuations={
        data.spargeEventSeries
          ? [
              {
                series: data.spargeEventSeries.map((y: number, x: number) => ({
                  x,
                  y,
                })),
                colour: bwColours.accent.orange,
                opacity: 0.1,
              },
            ]
          : undefined
      }
      setPoint={!mini && !record.endTime ? setpoint : undefined}
      units="mV"
      yMin={-100}
      yMax={200}
      yLabel="Redox (mV)"
      colour={bwColours.accent.red}
      mini={mini}
      predictionSeries={undefined}
      referenceSeries={undefined}
    />
  );
};

export const SpargePlot = ({ mini }) => {
  const record = useRecordContext();
  if (!record) return null;
  const data = record.data;

  if (!data.spargeVolumeSeries) return;

  return (
    <FermentPlot
      startTime={new Date(data.startTime)}
      xMax={calculateXmax(data)}
      dataSeries={data.spargeVolumeSeries.map((y: number, x: number) => ({
        x: x,
        y: y,
      }))}
      // Sparge actuation display of sparge events
      actuations={
        data.spargeEventSeries
          ? [
              {
                series: data.spargeEventSeries.map((y: number, x: number) => ({
                  x,
                  y,
                })),
                colour: bwColours.accent.orange,
                opacity: 0.1,
              },
            ]
          : undefined
      }
      units="L/hr"
      yMin={0}
      yMax={100}
      yLabel="Sparge Volume"
      colour={bwColours.accent.teal}
      mini={mini}
      predictionSeries={undefined}
      referenceSeries={undefined}
      setPoint={undefined}
    />
  );
};

export const PumpoverPlot = ({ mini }) => {
  const record = useRecordContext();
  if (!record) return null;
  const data = record.data;

  if (!data.pumpoverStateSeries) return;

  const series = data.pumpoverStateSeries.map((y: number, x: number) => ({
    x: x,
    y: y,
  }));

  return (
    <FermentPlot
      startTime={new Date(data.startTime)}
      xMax={calculateXmax(data)}
      dataSeries={[]}
      actuations={[
        { series, colour: bwColours.primary.offblack, opacity: 0.2 },
      ]}
      units=""
      yMin={0}
      yMax={1}
      yLabel="Pumpover"
      colour={bwColours.primary.offblack}
      mini={mini}
      predictionSeries={undefined}
      referenceSeries={undefined}
      setPoint={undefined}
    />
  );
};

// A dropdown over the analysis types that have data in this ferment (the
// analysis_types resource is filtered on the ferment's lot-vessel
// association server-side), plus a plot of all the analyses of the selected
// type. The plot auto-ranges its y-axis, since the range of an arbitrary
// analyte is not known up front.
export const AnalysisTypePlot = ({ label: _label }: { label?: string }) => {
  const record = useRecordContext();
  const [selectedTypeID, setSelectedTypeID] = useState<number | undefined>(
    undefined,
  );

  // The analysis types that have at least one analysis in this ferment.
  const { data: analysisTypes, isPending } = useGetList(
    "analysis_types",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "analyte", order: "ASC" },
      filter: { fermentID: record?.id },
    },
    { enabled: !!record?.id },
  );

  // All the analyses of the selected type in this ferment, oldest first.
  const { data: analyses } = useGetList(
    "analyses",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "timestamp", order: "ASC" },
      filter: { fermentID: record?.id, analysisTypeID: selectedTypeID },
    },
    { enabled: !!record?.id && selectedTypeID !== undefined },
  );

  if (!record) return null;
  const data = record.data;

  const types = analysisTypes ?? [];

  // A ferment without analyses has nothing to plot.
  if (!isPending && types.length === 0) return null;

  const selectedType = types.find((t) => t.id === selectedTypeID);

  // Hours relative to the start of the ferment, like the density reference
  // series in the Overview tab: the start of the device data when the
  // ferment has any, otherwise the start of the ferment itself (ferments
  // without a smart tank have no data start time).
  const startTime = new Date(data?.startTime ?? record.startTime);
  if (isNaN(startTime.getTime())) return null;
  const series: DataPoint[] = (analyses ?? [])
    .filter((a) => a.timestamp != null && a.value != null)
    .map((a) => ({
      x: (new Date(a.timestamp).getTime() - startTime.getTime()) / 1000 / 3600,
      y: a.value,
    }))
    .sort((a, b) => a.x - b.x);

  const unit = selectedType?.unit ?? "";
  // "Analyte (unit)"; safe when nothing is selected yet (or the selected
  // type disappeared from the list, in which case the plot is hidden).
  const yLabel = selectedType
    ? unit
      ? `${selectedType.analyte} (${unit})`
      : selectedType.analyte
    : "";

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 1 }}>
        <Typography variant="subtitle2">Plot Analyses:</Typography>
        <Select
          size="small"
          value={selectedTypeID !== undefined ? String(selectedTypeID) : ""}
          onChange={(e) =>
            setSelectedTypeID(
              e.target.value === "" ? undefined : Number(e.target.value),
            )
          }
        >
          <MenuItem value="">None</MenuItem>
          {types.map((t) => (
            <MenuItem key={t.id} value={String(t.id)}>
              {t.unit ? `${t.analyte} (${t.unit})` : t.analyte}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {selectedType && series.length > 0 ? (
        // Shown as reference points (black dots, no line) like the density
        // lab measurements, since these are sparse manual readings.
        <FermentPlot
          startTime={startTime}
          xMax={calculateXmax(
            data,
            Math.round(d3.max(series.map((d: DataPoint) => d.x)) ?? 0),
          )}
          dataSeries={undefined}
          units={unit}
          yLabel={yLabel}
          colour={bwColours.accent.mauve}
          mini={false}
          predictionSeries={undefined}
          referenceSeries={series}
        />
      ) : null}
    </div>
  );
};
